/**
 * Punto de entrada.
 *
 * Todo lo que puede fallar antes de que React tenga algo que mostrar se maneja
 * aca: configuracion faltante y ausencia del nodo raiz. La alternativa (dejar
 * que explote adentro del arbol) le deja al operador una pantalla en blanco y
 * un error en la consola, que en un panel interno equivale a nada.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import { createSupabaseClient } from "./auth/supabase";
import { ConfigError, loadConfig } from "./config";
import "./styles.css";

const container = document.getElementById("root");
if (container === null) {
  throw new Error('Falta el nodo #root en index.html');
}

const root = createRoot(container);

try {
  const config = loadConfig();
  const supabase = createSupabaseClient(config);

  // Orden del arbol: router -> sesion -> app. El router va afuera porque las
  // redirecciones del login (`<Navigate>`) y el menu (`<NavLink>`) necesitan su
  // contexto, y porque `AuthProvider` no usa ningun hook de routing: ponerlo al
  // reves solo agregaria una restriccion sin ganar nada.
  //
  // `<BrowserRouter>` sin `basename`: el panel se sirve desde la raiz del
  // dominio de Cloudflare Pages. Si algun dia colgara de un subpath, esto y el
  // `base` de `vite.config.ts` tienen que cambiar juntos.
  root.render(
    <StrictMode>
      <BrowserRouter>
        <AuthProvider config={config} supabase={supabase}>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </StrictMode>,
  );
} catch (error) {
  if (!(error instanceof ConfigError)) throw error;

  // Pantalla de configuracion faltante: dice exactamente que variable falta y
  // donde se define. Es lo unico util que se puede mostrar sin poder hablar ni
  // con Supabase ni con la API.
  root.render(
    <StrictMode>
      <main className="app__boot">
        <div className="feedback feedback--error" role="alert">
          <p>
            <strong>El panel no esta configurado.</strong>
          </p>
          <p>
            Faltan estas variables de entorno: <code>{error.missing.join(", ")}</code>
          </p>
          <p>
            En desarrollo se definen en <code>web/.env.local</code> (copiar de{" "}
            <code>web/env.example</code>). En produccion, en Cloudflare Pages &rarr;
            Settings &rarr; Environment variables.
          </p>
        </div>
      </main>
    </StrictMode>,
  );
}
